import DOMPurify from "dompurify";

import { BuilderJsonObject, findModules, Module } from "utilities";
import { BLANK_BEE_TEMPLATE } from "utilities/constants";

import {
  BACKEND_RUNTIME_SCRIPT_MARKERS,
  BASE_ADD_TAGS,
  MODULE_SELECTOR,
  PREVIEW_MODULE_ID_ATTRIBUTE,
  PREVIEW_PHONE_SCRIPT_PATH,
  PREVIEW_PHONE_STYLESHEET_PATH,
} from "../config";
import {
  buildMeasurementScript,
  buildNavigationShimScript,
  buildPhoneFieldScript,
  buildStorageShimScript,
} from "./iframe-runtime-scripts";

import { BuildPreviewSrcDocArgs } from "../types";

/**
 * Builds the sandboxed preview iframe's `srcdoc`. Security boundary is the opaque-origin sandbox
 * (`allow-scripts`, NO `allow-same-origin`) + the meta-CSP, NOT DOMPurify. With `executeScripts` the
 * touchpoint's author scripts run and the backend live-page runtime is stripped; without it DOMPurify drops
 * all scripts so the page renders statically — every block visible at once and commentable. The measurement
 * script is injected after sanitization and always runs. T3 guardrails (`injectStorageShim`,
 * `injectNavigationShim`, `injectBaseHref`) stop author scripts from being halted by opaque-origin
 * `SecurityError`s (storage/cookie/history) or from navigating the preview away. The one stripped piece the
 * preview puts back is the phone field's flag dropdown (`injectPhoneFieldScript`) — appearance the reviewer
 * is judging, without the form logic behind it. It is the one script every mode runs besides the measurement
 * script, so the static modes show the field as published too; only its dropdown follows `allowLinks`.
 *
 * `executeScripts` and `allowLinks` are deliberately independent, which is what gives the header its two
 * switches: comment mode turns both off (annotation only), "Full content" turns off only scripts so a
 * reviewer can see a whole quiz and still click its links, and plain preview leaves both on.
 */

const annotateModules = (doc: Document, htmlJSON: string): void => {
  let modules: Module[] = [];
  try {
    const parsedJson = JSON.parse(htmlJSON || BLANK_BEE_TEMPLATE) as BuilderJsonObject;
    modules = findModules(parsedJson);
  } catch {
    modules = [];
  }

  if (modules.length === 0) {
    return;
  }

  // Keep only OUTERMOST wrappers: BEE nests module classnames, so a flat querySelectorAll matches
  // descendants too and drifts the id↔node zip (the confirmed LP misalignment). Stamp the stable
  // annotation id (`descriptor.id`).
  const allNodes = Array.from(doc.querySelectorAll(MODULE_SELECTOR));
  const outermostNodes = allNodes.filter(
    (node) => !allNodes.some((other) => other !== node && other.contains(node)),
  );

  outermostNodes.forEach((node, index) => {
    const moduleId = modules[index]?.descriptor?.id;
    if (moduleId) {
      node.setAttribute(PREVIEW_MODULE_ID_ATTRIBUTE, moduleId);
    }
  });
};

const injectMeasurementScript = (doc: Document, nonce: string, allowLinks: boolean): void => {
  const script = doc.createElement("script");
  script.textContent = buildMeasurementScript(nonce, allowLinks);
  (doc.body ?? doc.documentElement).appendChild(script);
};

/**
 * DOMPurify's SAFE_FOR_XML mXSS probe removes any text-only element whose content matches `/<[/\w!]/`. For a
 * `<script>` that is every author script that builds markup in a string (`` `<li>…</li>` ``) or writes
 * `i<len` — dropped whole, with nothing in the console. Scripts are allowed on purpose here, so their source
 * is set aside while DOMPurify inspects the element and restored once it has passed. A private instance keeps
 * these hooks off the app-wide DOMPurify singleton.
 */
const heldScriptSources = new WeakMap<Node, string>();

const previewPurifier = DOMPurify(window);

previewPurifier.addHook("uponSanitizeElement", (node, { tagName }) => {
  if (tagName !== "script") {
    return;
  }
  heldScriptSources.set(node, node.textContent ?? "");
  node.textContent = "";
});

previewPurifier.addHook("afterSanitizeElements", (node) => {
  const source = heldScriptSources.get(node);
  if (source === undefined) {
    return;
  }
  node.textContent = source;
  heldScriptSources.delete(node);
});

const buildSanitizeConfig = (executeScripts: boolean) => ({
  WHOLE_DOCUMENT: true,
  ADD_TAGS: executeScripts ? [...BASE_ADD_TAGS, "script"] : BASE_ADD_TAGS,
  ADD_ATTR: ["target", "rel", "href", "sizes", "media", "as", "crossorigin", "type", "srcset", "charset"],
});

const stripBackendRuntimeScripts = (doc: Document): void => {
  doc.querySelectorAll("script").forEach((script) => {
    if (script.src) {
      return;
    }
    const text = script.textContent ?? "";
    if (BACKEND_RUNTIME_SCRIPT_MARKERS.some((marker) => text.includes(marker))) {
      script.remove();
    }
  });
};

/**
 * The library URL, derived from the backend's stylesheet link so any assets-host path segment survives.
 * A relative or non-http(s) href yields nothing — the backend writes an absolute assets URL, and anything
 * else is author-supplied markup we won't load a script from.
 */
const resolvePhoneLibraryUrl = (doc: Document): string | undefined => {
  const href =
    doc.querySelector(`link[href*="${PREVIEW_PHONE_STYLESHEET_PATH}"]`)?.getAttribute("href") ?? "";
  if (!href.includes(PREVIEW_PHONE_STYLESHEET_PATH)) {
    return undefined;
  }
  try {
    const { protocol } = new URL(href);
    if (protocol !== "http:" && protocol !== "https:") {
      return undefined;
    }
  } catch {
    return undefined;
  }

  return href.replace(PREVIEW_PHONE_STYLESHEET_PATH, PREVIEW_PHONE_SCRIPT_PATH);
};

const injectPhoneFieldScript = (doc: Document, allowDropdown: boolean): void => {
  if (!doc.querySelector("input[type='tel']")) {
    return;
  }
  const libraryUrl = resolvePhoneLibraryUrl(doc);
  if (!libraryUrl) {
    return;
  }
  const script = doc.createElement("script");
  script.textContent = buildPhoneFieldScript(libraryUrl, allowDropdown);
  (doc.body ?? doc.documentElement).appendChild(script);
};

const injectStorageShim = (doc: Document): void => {
  const head = doc.head ?? doc.documentElement;
  const script = doc.createElement("script");
  script.textContent = buildStorageShimScript();
  head.insertBefore(script, head.firstChild);
};

const injectNavigationShim = (doc: Document): void => {
  const head = doc.head ?? doc.documentElement;
  const script = doc.createElement("script");
  script.textContent = buildNavigationShimScript();
  head.insertBefore(script, head.firstChild);
};

/** Injects a `<base href>` for an absolute http(s) URL; returns its origin so the CSP can whitelist it (else undefined). */
const injectBaseHref = (doc: Document, baseHref?: string): string | undefined => {
  if (!baseHref) {
    return undefined;
  }
  let origin: string;
  try {
    const url = new URL(baseHref);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return undefined;
    }
    origin = url.origin;
  } catch {
    return undefined;
  }
  const head = doc.head ?? doc.documentElement;
  const base = doc.createElement("base");
  base.setAttribute("href", `${origin}/`);
  head.insertBefore(base, head.firstChild);
  return origin;
};

/** Defense-in-depth CSP. `base-uri` is narrowed to the injected `<base>` origin, else `'none'`. */
const buildPreviewMetaCsp = (baseUri?: string): string =>
  `connect-src 'none'; form-action 'none'; base-uri ${baseUri ?? "'none'"}`;

export const PREVIEW_META_CSP = buildPreviewMetaCsp();

const injectContentSecurityPolicy = (doc: Document, baseUri?: string): void => {
  const head = doc.head ?? doc.documentElement;
  const meta = doc.createElement("meta");
  meta.setAttribute("http-equiv", "Content-Security-Policy");
  meta.setAttribute("content", buildPreviewMetaCsp(baseUri));
  head.insertBefore(meta, head.firstChild);
};

/**
 * The parent sizes the iframe to the measured content height and scrolls the outer page, so the frame
 * itself must never show a scrollbar. BEE (and many email templates) set `overflow: auto` / `height: 100%`
 * on html/body — that creates a document scrollbar when the measured height is even 1px short, or a
 * scrollbar↔reflow feedback loop. Hide overflow so the preview chrome stays clean.
 */
const injectOverflowReset = (doc: Document): void => {
  const head = doc.head ?? doc.documentElement;
  const style = doc.createElement("style");
  style.setAttribute("data-tp-preview-overflow", "");
  style.textContent = "html, body { overflow: hidden !important; }";
  head.appendChild(style);
};

export const buildPreviewSrcDoc = ({
  finalHtml,
  htmlJSON,
  nonce,
  executeScripts = false,
  allowLinks = executeScripts,
  baseHref,
}: BuildPreviewSrcDocArgs): string => {
  const sanitizedHtml = previewPurifier.sanitize(finalHtml || "", buildSanitizeConfig(executeScripts));
  const doc = new DOMParser().parseFromString(sanitizedHtml, "text/html");
  // Shims must run before author scripts (prepended before the CSP meta); nav first so storage stays first.
  if (executeScripts) {
    stripBackendRuntimeScripts(doc);
    injectNavigationShim(doc);
    injectStorageShim(doc);
  }
  // Appended to the body, so it runs with the phone field already parsed. In comment mode the flag is drawn
  // but the dropdown stays shut so it cannot compete with click-to-annotate.
  injectPhoneFieldScript(doc, allowLinks);
  const baseOrigin = injectBaseHref(doc, baseHref);
  injectContentSecurityPolicy(doc, baseOrigin);
  injectOverflowReset(doc);
  annotateModules(doc, htmlJSON);
  // Links open in a new tab whenever `allowLinks` — including with author scripts stripped; only comment
  // mode blocks every link click.
  injectMeasurementScript(doc, nonce, allowLinks);
  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
};
